/**
 * Light photo reduction at capture (#916): keeps a phone from holding
 * multi-megapixel bodies in memory. Anything that cannot be reduced
 * safely is returned as the original.
 */

/** Longest side, in pixels, a new photo is reduced to. */
export const MAX_LONG_SIDE = 2500;
/** JPEG quality used when re-encoding. */
export const JPEG_QUALITY = 0.85;

interface Decoded {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

async function decode(file: Blob): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    // The TypeScript DOM lib in use predates 'from-image'; the runtime value is correct.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions);
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      release: () => (bitmap as { close?: () => void }).close?.(),
    };
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('image decode failed'));
      el.src = url;
    });
    return {
      source: img,
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
      release: () => undefined,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Reduce a photo so its long side is at most MAX_LONG_SIDE, as JPEG.
 * Returns the original when it is already small, or when decoding,
 * encoding fails, or the result is not smaller.
 */
export async function reducePhoto(file: Blob): Promise<Blob> {
  let decoded: Decoded | null = null;
  let canvas: HTMLCanvasElement | null = null;
  try {
    decoded = await decode(file);
    const { width, height } = decoded;
    if (!width || !height || Math.max(width, height) <= MAX_LONG_SIDE) return file;
    const scale = MAX_LONG_SIDE / Math.max(width, height);
    canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(decoded.source, 0, 0, canvas.width, canvas.height);
    const c = canvas;
    const out = await new Promise<Blob | null>((resolve) =>
      c.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    );
    if (!out || out.size >= file.size) return file;
    return out;
  } catch {
    return file;
  } finally {
    try { decoded?.release(); } catch { /* ignore */ }
    if (canvas) { canvas.width = 0; canvas.height = 0; }
  }
}
